/**
 * The screen-flash step: the whole screen shows the server's colour sequence
 * while a short clip records the face it lights. The sequence is the nonce —
 * drawn by the server seconds ago — so a recording made earlier cannot match.
 *
 * Timing contract with the sidecar (`flash.py::MAX_OFFSET_MS`): the recorder
 * starts and the first colour paints in the SAME tick, so the clip may begin a
 * little AFTER the first colour (recorder latency), never before it.
 *
 * Safety: segments are ≥ 450 ms (server-enforced), so at most ~2 changes a
 * second — under WCAG 2.3.1's three — and colours ease over 150 ms rather than
 * snapping. "I'm sensitive to flashing light" stops it at once; the server then
 * records the check as not done, which changes no verdict.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { FlashSegment } from './types';

const PAINT: Record<FlashSegment['color'], string> = {
  blue: 'rgb(30, 60, 255)',
  green: 'rgb(20, 200, 60)',
  red: 'rgb(240, 40, 40)',
};
/** Kept past the last colour so the final segment is fully in the clip. */
const TAIL_MS = 150;

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () =>
      resolve(typeof reader.result === 'string' ? (reader.result.split(',')[1] ?? '') : '');
    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
    reader.readAsDataURL(blob);
  });
}

export function FlashStage({
  onDone,
  sequence,
  stream,
}: {
  /** The clip as base64, or null when it could not be recorded or was skipped. */
  onDone: (clipBase64: null | string) => void;
  sequence: FlashSegment[];
  stream: MediaStream | null;
}) {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);
  const finished = useRef(false);
  const recorderRef = useRef<MediaRecorder | null>(null);

  const finish = (clip: null | string) => {
    if (finished.current) return;
    finished.current = true;
    onDone(clip);
  };

  useEffect(() => {
    const chunks: Blob[] = [];
    let recorder: MediaRecorder | null = null;
    if (stream && typeof MediaRecorder !== 'undefined') {
      try {
        recorder = new MediaRecorder(stream);
        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) chunks.push(e.data);
        };
        recorder.onstop = () => {
          const blob = new Blob(chunks, { type: recorder?.mimeType || 'video/webm' });
          blobToBase64(blob).then(
            (b64) => finish(b64 || null),
            () => finish(null),
          );
        };
        recorder.start();
        recorderRef.current = recorder;
      } catch {
        recorder = null;
      }
    }
    const timers: number[] = [];
    let at = 0;
    sequence.forEach((segment, i) => {
      timers.push(window.setTimeout(() => setIndex(i), at));
      at += segment.ms;
    });
    timers.push(
      window.setTimeout(() => {
        if (recorder && recorder.state !== 'inactive') recorder.stop();
        else finish(null);
      }, at + TAIL_MS),
    );
    return () => {
      timers.forEach((id) => window.clearTimeout(id));
      if (recorder && recorder.state !== 'inactive') recorder.stop();
    };
    // Runs once per issued sequence.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sequence]);

  const skip = () => {
    const recorder = recorderRef.current;
    finished.current = true; // the clip of a skipped sequence is not sent
    if (recorder && recorder.state !== 'inactive') recorder.stop();
    onDone(null);
  };

  const color = sequence[index]?.color ?? 'blue';
  return (
    <div
      className="flash-stage"
      data-color={color}
      data-testid="flash-stage"
      style={{ backgroundColor: PAINT[color] }}
    >
      <p className="flash-stage__title">{t('flow.liveness.flash.title')}</p>
      <button className="flash-stage__skip" data-track="skip_flash" onClick={skip} type="button">
        {t('flow.liveness.flash.skip')}
      </button>
    </div>
  );
}
